import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import useAxiosInstance from '../../hooks/useAxios';

const NewsletterEditor = () => {
  const [data, setData] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('themes');
  const [uuid, setUuid] = useState('');
  const [loading, setLoading] = useState(false);
  const axiosInstance = useAxiosInstance();

  const parseJson = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      setData(parsed);
      setSelectedTheme(parsed[0] || null);
      setSelectedArticle(null);
      setError(null);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  const loadNewsletterData = async () => {
    if (!uuid.trim()) {
      toast.error('Please enter a newsletter UUID');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post('/newsletter/v2/get_newsletter_details/', {
        newsletter_id: uuid
      });

      const details = res.data?.data?.details || [];
      parseJson(JSON.stringify(details));
      toast.success('Newsletter data loaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to load newsletter data';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const uploadNewsletterData = async () => {
    if (!uuid.trim()) {
      toast.error('Please enter a newsletter UUID');
      return;
    }

    if (!data) {
      toast.error('No data to upload. Please load or create newsletter data first.');
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post('/newsletter/v2/upload_poc_newsletter/', {
        uuid_of_newsletter: uuid,
        data: data,
        reference_id: 0,
        workflow: 'newsletter'
      });

      toast.success('Newsletter data uploaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to upload newsletter data';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setJsonInput(event.target.result);
        parseJson(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleJsonTextChange = (e) => {
    setJsonInput(e.target.value);
  };

  const handleApplyJson = () => {
    parseJson(jsonInput);
  };

  const updateData = useCallback((updater) => {
    setData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setJsonInput(JSON.stringify(next, null, 2));
      return next;
    });
  }, []);

  const addTheme = () => {
    const newTheme = {
      theme_name: "new-theme",
      details: {
        keythemes: [],
        articles: []
      }
    };
    updateData(prev => [...prev, newTheme]);
    setSelectedTheme(newTheme);
  };

  const removeTheme = (index) => {
    updateData(prev => prev.filter((_, i) => i !== index));
    setSelectedTheme(null);
    setSelectedArticle(null);
    toast.success('Theme deleted');
  };

  const updateThemeName = (index, newName) => {
    updateData(prev => prev.map((theme, i) =>
      i === index ? { ...theme, theme_name: newName } : theme
    ));
  };

  const addKeyThemePoint = (themeIndex) => {
    const newPoint = { point: "New point text here" };
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, keythemes: [...theme.details.keythemes, newPoint] } }
        : theme
    ));
  };

  const removeKeyThemePoint = (themeIndex, pointIndex) => {
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, keythemes: theme.details.keythemes.filter((_, pi) => pi !== pointIndex) } }
        : theme
    ));
    toast.success('Key theme point deleted');
  };

  const updateKeyThemePoint = (themeIndex, pointIndex, newValue) => {
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, keythemes: theme.details.keythemes.map((p, pi) => pi === pointIndex ? { point: newValue } : p) } }
        : theme
    ));
  };

  const addArticle = (themeIndex) => {
    const newArticle = {
      id: "NEW" + Date.now(),
      title: "New Article Title",
      author: "",
      url: "",
      publisher: "",
      publishedDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      source: "Google",
      category: "",
      flag_processed: false,
      extra_info: { primary_tag: "", reject_reason: "", secondary_tags: [] },
      dt_createdOn: new Date().toISOString().split('T')[0],
      dt_lastUpdatedOn: new Date().toISOString().split('T')[0],
      type: "",
      img: "",
      scraped_text: "",
      domain: "",
      news_type: "",
      snippet: "",
      source_type: "others",
      about_topic: true,
      topic: "",
      theme_name: [],
      name: [],
      prod_cat_arr: [],
      ai_results_summarised: [],
      monitoring_type: null
    };
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, articles: [...theme.details.articles, newArticle] } }
        : theme
    ));
    setSelectedArticle(newArticle);
  };

  const removeArticle = (themeIndex, articleIndex) => {
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, articles: theme.details.articles.filter((_, ai) => ai !== articleIndex) } }
        : theme
    ));
    setSelectedArticle(null);
    toast.success('Article deleted');
  };

  const updateArticleField = (themeIndex, articleIndex, field, value) => {
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, articles: theme.details.articles.map((article, ai) => ai === articleIndex ? { ...article, [field]: value } : article) } }
        : theme
    ));
  };

  const updateExtraInfo = (themeIndex, articleIndex, field, value) => {
    updateData(prev => prev.map((theme, i) =>
      i === themeIndex
        ? { ...theme, details: { ...theme.details, articles: theme.details.articles.map((article, ai) => ai === articleIndex ? { ...article, extra_info: { ...article.extra_info, [field]: value } } : article) } }
        : theme
    ));
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter-editor.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentThemeIndex = selectedTheme ? data.findIndex(t => t.theme_name === selectedTheme.theme_name) : -1;
  const currentArticleIndex = selectedArticle ? data[currentThemeIndex]?.details?.articles?.findIndex(a => a.id === selectedArticle.id) : -1;

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Newsletter JSON Editor</h1>
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-xl font-semibold mb-6">Load Newsletter Data</h2>

          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">Newsletter UUID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={uuid}
                onChange={(e) => setUuid(e.target.value)}
                placeholder="Enter newsletter UUID..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={loadNewsletterData}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:bg-gray-400"
              >
                {loading ? 'Loading...' : 'Load'}
              </button>
            </div>
          </div>

          <div className="text-center text-gray-500 my-4 font-bold">OR</div>

          <h3 className="text-lg font-semibold mb-4">Upload JSON Manually</h3>
          <div className="mb-4">
            <input type="file" accept=".json" onChange={handleFileUpload} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-2 file:border-dashed file:border-gray-400 hover:file:border-blue-500" />
          </div>
          <div className="text-center text-gray-500 my-4 font-bold">OR</div>
          <textarea
            value={jsonInput}
            onChange={handleJsonTextChange}
            placeholder="Paste JSON here..."
            className="w-full h-64 p-3 font-mono text-sm border border-gray-300 rounded-lg mb-4"
          />
          {error && <div className="text-red-600 bg-red-100 p-3 rounded mb-4">{error}</div>}
          <button onClick={handleApplyJson} className="w-full bg-blue-600 text-white py-3 px-6 rounded font-bold hover:bg-blue-700 transition">
            Apply JSON
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Newsletter JSON Editor</h1>
          {uuid && <p className="text-sm text-gray-600 mt-1">UUID: {uuid}</p>}
        </div>
        <div className="flex gap-3">
          <button
            onClick={uploadNewsletterData}
            disabled={loading}
            className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700 transition disabled:bg-gray-400"
          >
            {loading ? 'Uploading...' : 'Upload to Newsletter'}
          </button>
          <button onClick={handleExport} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 transition">
            Export JSON
          </button>
          <button onClick={() => { setData(null); setSelectedTheme(null); setSelectedArticle(null); setJsonInput(''); setUuid(''); }} className="bg-gray-600 text-white px-4 py-2 rounded font-bold hover:bg-gray-700 transition">
            Clear Data
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-140px)]">
        <div className="col-span-3 bg-white rounded-lg shadow flex flex-col overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-bold text-lg">Themes ({data.length})</h2>
            <button onClick={addTheme} className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-blue-700 transition">
              + Add
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {data.map((theme, index) => (
              <div
                key={index}
                onClick={() => { setSelectedTheme(theme); setSelectedArticle(null); }}
                className={`p-3 rounded cursor-pointer transition flex justify-between items-center ${selectedTheme?.theme_name === theme.theme_name ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'}`}
              >
                <div className="overflow-hidden">
                  <span className="font-bold text-sm block truncate">{theme.theme_name}</span>
                  <span className="text-xs text-gray-500">
                    {theme.details?.keythemes?.length || 0} pts, {theme.details?.articles?.length || 0} articles
                  </span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); removeTheme(index); }} className="bg-red-500 text-white w-6 h-6 rounded text-sm font-bold hover:bg-red-600 ml-2">
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-5 bg-white rounded-lg shadow overflow-hidden flex flex-col">
          {selectedTheme ? (
            <>
              <div className="p-4 border-b">
                <label className="block text-sm font-bold text-gray-700 mb-1">Theme Name</label>
                <input
                  type="text"
                  value={selectedTheme.theme_name}
                  onChange={(e) => {
                    const idx = data.findIndex(t => t.theme_name === selectedTheme.theme_name);
                    updateThemeName(idx, e.target.value);
                    setSelectedTheme({ ...selectedTheme, theme_name: e.target.value });
                  }}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab('themes')}
                  className={`flex-1 py-3 font-bold text-sm transition ${activeTab === 'themes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Key Themes ({selectedTheme.details?.keythemes?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('articles')}
                  className={`flex-1 py-3 font-bold text-sm transition ${activeTab === 'articles' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Articles ({selectedTheme.details?.articles?.length || 0})
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {activeTab === 'themes' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold">Key Theme Points</h3>
                      <button onClick={() => addKeyThemePoint(currentThemeIndex)} className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-green-700 transition">
                        + Add Point
                      </button>
                    </div>
                    <div className="space-y-3">
                      {selectedTheme.details?.keythemes?.map((point, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded border">
                          <div className="flex justify-between mb-2">
                            <span className="font-bold text-blue-600">#{index + 1}</span>
                            <button onClick={() => removeKeyThemePoint(currentThemeIndex, index)} className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold hover:bg-red-600">
                              Delete
                            </button>
                          </div>
                          <textarea
                            value={point.point || ''}
                            onChange={(e) => updateKeyThemePoint(currentThemeIndex, index, e.target.value)}
                            className="w-full p-2 font-mono text-sm border border-gray-300 rounded resize-y"
                            rows={4}
                          />
                        </div>
                      ))}
                      {(!selectedTheme.details?.keythemes || selectedTheme.details.keythemes.length === 0) && (
                        <p className="text-gray-400 text-center py-8 italic">No key themes yet. Click "Add Point" to create one.</p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'articles' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold">Articles</h3>
                      <button onClick={() => addArticle(currentThemeIndex)} className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-green-700 transition">
                        + Add Article
                      </button>
                    </div>
                    <div className="space-y-2">
                      {selectedTheme.details?.articles?.map((article, index) => (
                        <div
                          key={index}
                          onClick={() => setSelectedArticle(article)}
                          className={`p-3 rounded cursor-pointer transition ${selectedArticle?.id === article.id ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'}`}
                        >
                          <div className="flex justify-between items-start">
                            <span className="font-bold text-sm">{article.title || 'Untitled'}</span>
                            <button onClick={(e) => { e.stopPropagation(); removeArticle(currentThemeIndex, index); }} className="bg-red-500 text-white w-5 h-5 rounded text-xs font-bold hover:bg-red-600 ml-2">
                              ×
                            </button>
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span>{article.publisher || 'No publisher'}</span>
                            <span>{article.date || 'No date'}</span>
                          </div>
                        </div>
                      ))}
                      {(!selectedTheme.details?.articles || selectedTheme.details.articles.length === 0) && (
                        <p className="text-gray-400 text-center py-8 italic">No articles yet. Click "Add Article" to create one.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 italic">
              Select a theme from the sidebar to edit it.
            </div>
          )}
        </div>

        <div className="col-span-4 bg-white rounded-lg shadow flex flex-col overflow-hidden">
          {selectedArticle ? (
            <>
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-bold">Edit Article</h3>
                <button onClick={() => setSelectedArticle(null)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Title</label>
                  <input type="text" value={selectedArticle.title || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'title', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Author</label>
                  <input type="text" value={selectedArticle.author || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'author', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">URL</label>
                  <input type="text" value={selectedArticle.url || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'url', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Publisher</label>
                  <input type="text" value={selectedArticle.publisher || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'publisher', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Date</label>
                  <input type="text" value={selectedArticle.date || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'date', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Category</label>
                  <input type="text" value={selectedArticle.category || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'category', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                  <input type="text" value={selectedArticle.type || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'type', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Image URL</label>
                  <input type="text" value={selectedArticle.img || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'img', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Domain</label>
                  <input type="text" value={selectedArticle.domain || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'domain', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">News Type</label>
                  <input type="text" value={selectedArticle.news_type || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'news_type', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Topic</label>
                  <input type="text" value={selectedArticle.topic || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'topic', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Extra Info - Primary Tag</label>
                  <input type="text" value={selectedArticle.extra_info?.primary_tag || ''} onChange={(e) => updateExtraInfo(currentThemeIndex, currentArticleIndex, 'primary_tag', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Extra Info - Reject Reason</label>
                  <input type="text" value={selectedArticle.extra_info?.reject_reason || ''} onChange={(e) => updateExtraInfo(currentThemeIndex, currentArticleIndex, 'reject_reason', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Scraped Text</label>
                  <textarea value={selectedArticle.scraped_text || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'scraped_text', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded resize-y" rows={4} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Snippet</label>
                  <textarea value={selectedArticle.snippet || ''} onChange={(e) => updateArticleField(currentThemeIndex, currentArticleIndex, 'snippet', e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded resize-y" rows={3} />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 italic">
              {selectedTheme ? 'Select an article to edit its details.' : 'Select a theme and article to edit.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterEditor;